/**
 * Intake/Referral PDF Generator
 * 
 * Generates client referral PDFs using jsPDF library.
 * Extracted from App.js for better maintainability.
 */

// Helper to format date as MM-DD-YYYY for filenames
export const formatDateForFilename = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
};

// Helper to generate download filename
export const generateDownloadFilename = (client, documentType, extension = 'pdf') => {
  const nameParts = (client?.full_name || 'Unknown').trim().split(' ');
  const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || 'Unknown';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const formattedName = lastName ? `${lastName}, ${firstName}` : firstName;
  
  // Clean up document type and filename
  const cleanDocType = documentType.replace(/[<>:"/\\|?*]/g, '');
  const filename = `${formattedName} - ${cleanDocType}.${extension}`;
  
  return filename;
};

// Helper to format date as MM/DD/YYYY for display
export const formatDateMMDDYYYY = (dateString) => {
  if (!dateString) return null;
  
  // Parse date string as local date to avoid timezone conversion
  // Input format: YYYY-MM-DD
  if (typeof dateString === 'string' && dateString.includes('-')) {
    const parts = dateString.split('-');
    // Validate it looks like a date (YYYY-MM-DD)
    if (parts.length === 3 && parts[0].length === 4 && !isNaN(parseInt(parts[0]))) {
      const [year, month, day] = parts;
      return `${month}/${day}/${year}`;
    }
  }
  
  // Fallback for other date formats
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}/${day}/${year}`;
};

/**
 * Add branded organization header to PDF document
 * @param {jsPDF} doc - jsPDF document instance
 * @param {string} documentTitle - Title to display
 * @param {Object} brandingSettings - Organization branding settings
 * @param {Array} locations - Organization locations
 * @returns {number} Y position after header
 */
export const addPdfOrgHeader = async (doc, documentTitle, brandingSettings, locations) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // Find the primary location (is_primary flag) or fall back to first location
  const primaryLocation = locations?.find(loc => loc.is_primary) || locations?.[0] || {};
  
  let yPos = 10;
  
  // Add company name/logo
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(14, 116, 144);
  doc.text(brandingSettings?.company_display_name || brandingSettings?.company_name || 'Healthcare Practice', 15, yPos + 5);
  
  // Right side - Address info
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  
  let rightY = yPos;
  if (primaryLocation.address_line1) {
    let addressLine = primaryLocation.address_line1;
    if (primaryLocation.address_line2) addressLine += `, ${primaryLocation.address_line2}`;
    doc.text(addressLine, pageWidth - 15, rightY, { align: 'right' });
    rightY += 4;
  }
  
  if (primaryLocation.city || primaryLocation.state || primaryLocation.zip_code) {
    const cityStateZip = [primaryLocation.city, primaryLocation.state, primaryLocation.zip_code].filter(Boolean).join(', ');
    doc.text(cityStateZip, pageWidth - 15, rightY, { align: 'right' });
    rightY += 4;
  }
  
  if (primaryLocation.phone) {
    let contactLine = `Phone: ${primaryLocation.phone}`;
    if (primaryLocation.fax) contactLine += ` | Fax: ${primaryLocation.fax}`;
    doc.text(contactLine, pageWidth - 15, rightY, { align: 'right' });
    rightY += 4;
  }
  
  // Draw separator line
  yPos = Math.max(yPos + 12, rightY + 2);
  doc.setDrawColor(14, 116, 144);
  doc.setLineWidth(0.5);
  doc.line(15, yPos, pageWidth - 15, yPos);
  
  // Document title if provided
  if (documentTitle) {
    yPos += 8;
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(documentTitle, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }
  
  return yPos + 5; // Return the Y position to continue content from
};

/**
 * Add branded footer to PDF document
 * @param {jsPDF} doc - jsPDF document instance
 * @param {Object} brandingSettings - Organization branding settings
 */
export const addPdfOrgFooter = (doc, brandingSettings) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(128, 128, 128);
  
  let footerY = pageHeight - 15;
  
  // Draw separator line
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(15, footerY - 3, pageWidth - 15, footerY - 3);
  
  if (brandingSettings?.company_website) {
    doc.text(brandingSettings?.company_website, pageWidth / 2, footerY, { align: 'center' });
    footerY += 4;
  }
  
  if (brandingSettings?.company_motto) {
    doc.setFont(undefined, 'italic');
    doc.text(brandingSettings?.company_motto, pageWidth / 2, footerY, { align: 'center' });
  }
};

/**
 * Generate intake/referral PDF document
 * @param {Object} params - Generation parameters
 * @param {Object} params.intakeData - Client intake form data
 * @param {Object} params.editingClient - Client being edited (optional)
 * @param {Object} params.intakeFormConfig - Form configuration with steps
 * @param {Array} params.locations - Organization locations
 * @param {Object} params.brandingSettings - Organization branding settings
 * @returns {Promise<{blob: Blob, fileName: string}>} PDF blob and filename
 */
export const generateIntakePdf = async ({
  intakeData,
  editingClient,
  intakeFormConfig,
  locations,
  brandingSettings
}) => {
  // Import jsPDF dynamically
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  // Set up document
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Modern color scheme
  const primaryColor = [14, 116, 144]; // Teal
  const accentColor = [245, 247, 250]; // Light grey background
  const textColor = [31, 41, 55]; // Dark grey text
  
  // Add branded org header
  let yPos = await addPdfOrgHeader(doc, 'CLIENT REFERRAL FORM', brandingSettings, locations);
  
  // Add generation date and MRN
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...textColor);
  const currentDate = new Date().toLocaleDateString();
  doc.text(`Generated: ${currentDate}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  
  // Add MRN if available
  if (editingClient?.mrn || intakeData.mrn) {
    const mrn = (editingClient?.mrn || intakeData.mrn).replace('MRN-', '');
    doc.text(`MRN: #${mrn}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
  }
  
  yPos += 5;
  
  // Helper function to add modern section with styled layout
  const addSection = (title, rows) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Section header with colored background
    doc.setFillColor(...accentColor);
    doc.rect(12, yPos - 4, pageWidth - 24, 8, 'F');
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(title, 15, yPos);
    yPos += 8;
    
    // Reset text color for content
    doc.setTextColor(...textColor);
    doc.setFontSize(8.5);
    
    // Process each row
    rows.forEach(row => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Fields that should ALWAYS be rendered full-width (regardless of content length)
      // Be specific to avoid catching unwanted fields (e.g., "referral source" should NOT be full-width)
      const fullWidthLabels = [
        'reason for referral', 'reason_for_referral',
        'symptoms checklist', 'symptoms_checklist', 'symptom checklist',
        'presenting problem', 'presenting_problem',
        'additional notes', 'additional_notes',
        'clinical notes', 'progress notes',
        'treatment history', 'medical history', 'mental health history',
        'narrative', 'description'
      ];
      
      // Check each field to see if it needs full-width rendering
      const fieldsNeedFullWidth = row.map(field => {
        const val = field.value;
        const labelLower = (field.label || '').toLowerCase();
        
        // Check if this is a designated full-width field by exact match or close match
        const isFullWidthField = fullWidthLabels.some(fw => 
          labelLower === fw || labelLower.includes(fw) || fw.includes(labelLower)
        );
        
        // Check if content is long (more than 50 chars, has newlines, or multiple commas indicating a list)
        const hasLongValue = val && typeof val === 'string' && 
          (val.length > 50 || val.includes('\n') || (val.split(',').length > 3 && val.length > 30));
        
        return isFullWidthField || hasLongValue;
      });
      
      // If ANY field in this row needs full-width, render ALL fields in that row full-width
      const hasLongContent = fieldsNeedFullWidth.some(needs => needs);
      
      // If row has long content, render each field full width
      if (hasLongContent) {
        row.forEach((field) => {
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          
          const xPos = 15;
          const fullWidth = pageWidth - 30; // Full width with margins
          
          // Label (bold, slightly smaller, colored)
          doc.setTextColor(100, 116, 139); // Slate grey
          doc.setFontSize(7.5);
          doc.setFont(undefined, 'bold');
          doc.text(field.label.toUpperCase(), xPos, yPos);
          
          // Value with better styling
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
          let value = field.value;
          
          // Convert non-string values to string and handle empty values
          if (value === null || value === undefined) {
            value = '';
          } else if (typeof value !== 'string') {
            value = String(value);
          }
          
          if (!value || value.trim() === '') {
            // Empty value - modern italic style
            doc.setFont(undefined, 'italic');
            doc.setTextColor(156, 163, 175); // Light grey
            doc.setFontSize(8);
            value = 'Not provided';
            doc.text(value, xPos, yPos + 4);
            yPos += 11;
          } else {
            doc.setTextColor(...textColor);
            // Wrap text to full width and show ALL lines
            const wrappedValue = doc.splitTextToSize(value, fullWidth);
            wrappedValue.forEach((line, lineIdx) => {
              if (yPos + 4 + (lineIdx * 4) > 270) {
                doc.addPage();
                yPos = 20;
              }
              doc.text(line, xPos, yPos + 4 + (lineIdx * 4));
            });
            // Calculate total height used
            const textHeight = wrappedValue.length * 4;
            yPos += textHeight + 8; // Add spacing after field
          }
          
          // Subtle underline for field
          doc.setDrawColor(226, 232, 240); // Very light grey
          doc.setLineWidth(0.2);
          doc.line(xPos, yPos - 2, xPos + fullWidth, yPos - 2);
        });
      } else {
        // Normal row rendering (multiple columns)
        const colWidth = 180 / row.length;
        
        // Calculate the max number of lines needed for this row
        let maxLines = 1;
        const fieldData = row.map((field, index) => {
          const xPos = 15 + (index * colWidth);
          let value = field.value;
          
          // Convert non-string values to string and handle empty values
          if (value === null || value === undefined) {
            value = '';
          } else if (typeof value !== 'string') {
            value = String(value);
          }
          
          const isEmpty = !value || value.trim() === '';
          const displayValue = isEmpty ? 'Not provided' : value;
          const maxValueWidth = colWidth - 5;
          const wrappedValue = doc.splitTextToSize(displayValue, maxValueWidth);
          maxLines = Math.max(maxLines, wrappedValue.length);
          
          return { field, xPos, value, isEmpty, wrappedValue, maxValueWidth };
        });
        
        // Render each field
        fieldData.forEach(({ field, xPos, isEmpty, wrappedValue }) => {
          // Label (bold, slightly smaller, colored)
          doc.setTextColor(100, 116, 139); // Slate grey
          doc.setFontSize(7.5);
          doc.setFont(undefined, 'bold');
          doc.text(field.label.toUpperCase(), xPos, yPos);
          
          // Value with better styling
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
          
          if (isEmpty) {
            // Empty value - modern italic style
            doc.setFont(undefined, 'italic');
            doc.setTextColor(156, 163, 175); // Light grey
            doc.setFontSize(8);
          } else {
            doc.setTextColor(...textColor);
          }
          
          // Show ALL lines of wrapped text
          wrappedValue.forEach((line, lineIdx) => {
            doc.text(line, xPos, yPos + 4 + (lineIdx * 4));
          });
          
          // Subtle underline for field (after all text)
          const underlineY = yPos + 4 + (wrappedValue.length * 4) + 1;
          doc.setDrawColor(226, 232, 240); // Very light grey
          doc.setLineWidth(0.2);
          doc.line(xPos, underlineY, xPos + colWidth - 8, underlineY);
        });
        
        // Calculate row height based on max lines
        const rowHeight = Math.max(11, 8 + (maxLines * 4));
        yPos += rowHeight;
      }
    });
    
    yPos += 3; // Space after section
  };
  
  // Dynamic PDF generation based on intake form config
  if (intakeFormConfig && intakeFormConfig.steps) {
    // Fields that are shown elsewhere and should be excluded
    const excludeFields = ['assigned_providers', 'assignment_type', 'assigned_provider'];
    
    // Fields that should be grouped together on the same row
    const groupedFields = {
      'referral_source_contact_number': 'referral_source_email',  // phone and email on same row
      'referral_source_email': 'referral_source_contact_number'   // reverse mapping
    };
    
    intakeFormConfig.steps.forEach(section => {
      // Filter visible fields that should be included
      const visibleFields = section.fields.filter(field => 
        field.visible && !excludeFields.includes(field.field_id)
      );
      
      if (visibleFields.length === 0) return;
      
      // Group fields into rows (3 fields per row for better layout)
      // But keep certain fields together (like phone and email)
      const rows = [];
      const processedFields = new Set();
      
      for (let i = 0; i < visibleFields.length; i++) {
        const field = visibleFields[i];
        
        // Skip if already processed (as part of a group)
        if (processedFields.has(field.field_id)) continue;
        
        const rowFields = [];
        
        // Check if this field should be grouped with another
        const pairedFieldId = groupedFields[field.field_id];
        const pairedField = pairedFieldId ? visibleFields.find(f => f.field_id === pairedFieldId) : null;
        
        // Add current field
        rowFields.push(field);
        processedFields.add(field.field_id);
        
        // Add paired field if exists
        if (pairedField && !processedFields.has(pairedField.field_id)) {
          rowFields.push(pairedField);
          processedFields.add(pairedField.field_id);
        }
        
        // Fill remaining slots (up to 3 per row) with next available fields
        while (rowFields.length < 3) {
          const nextField = visibleFields.find(f => !processedFields.has(f.field_id));
          if (!nextField) break;
          
          // Don't add if this field has a pair that hasn't been processed
          const nextPairedFieldId = groupedFields[nextField.field_id];
          if (nextPairedFieldId && !processedFields.has(nextPairedFieldId)) {
            // Start a new row for this paired group
            break;
          }
          
          rowFields.push(nextField);
          processedFields.add(nextField.field_id);
        }
        
        // Convert to row format with values
        const formattedRow = rowFields.map(f => {
          let value = intakeData[f.field_id];
          
          // Handle different field types
          if (f.field_type === 'checkbox') {
            value = value ? 'Yes' : 'No';
          } else if (f.field_type === 'date' && value) {
            value = formatDateMMDDYYYY(value) || value;
          } else if ((f.field_id === 'preferred_location' || f.field_type === 'location_select') && value) {
            // Convert location UUID to name - check if it's already a name (not a UUID)
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
            if (isUUID) {
              const location = (locations || []).find(loc => loc.id === value);
              value = location ? location.name : value;
            }
            // If not a UUID, keep the value as-is (it's already a name)
          } else if (Array.isArray(value)) {
            value = value.join(', ');
          } else if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value);
          } else if (value !== null && value !== undefined && typeof value !== 'string') {
            value = String(value);
          }
          
          return {
            label: f.field_name,
            value: value
          };
        });
        
        rows.push(formattedRow);
      }
      
      addSection(section.step_name, rows);
    });
  } else {
    // Fallback if config not loaded - show basic info
    addSection('Basic Information', [
      [
        { label: 'First Name', value: intakeData.first_name },
        { label: 'Middle Name', value: intakeData.middle_name },
        { label: 'Last Name', value: intakeData.last_name }
      ],
      [
        { label: 'Preferred Name', value: intakeData.preferred_name },
        { label: 'Date of Birth', value: intakeData.date_of_birth },
        { label: 'Gender', value: intakeData.gender }
      ]
    ]);
  }

  // ── Referral safety net (Jul 2026) ────────────────────────────────
  // The intake dialog collects 7 referral fields, but the PDF is
  // driven by the org's SAVED intake form config — configs saved
  // before the referral step existed (or with those fields hidden)
  // silently dropped Referral Source from the PDF, and the extended
  // fields (organization / phone / email / type) were never in the
  // default config at all.  Render any referral field with a value
  // that the config-driven pass didn't already cover.
  const configRenderedIds = new Set();
  if (intakeFormConfig && intakeFormConfig.steps) {
    intakeFormConfig.steps.forEach(s => (s.fields || []).forEach(f => {
      if (f.visible) configRenderedIds.add(f.field_id);
    }));
  }
  const referralFieldDefs = [
    { id: 'referral_source', label: 'Referral Source' },
    { id: 'referral_type', label: 'Referral Type' },
    { id: 'referral_contact', label: 'Referral Contact' },
    { id: 'referral_organization', label: 'Referral Organization' },
    { id: 'referral_phone', label: 'Referral Phone' },
    { id: 'referral_email', label: 'Referral Email' },
    { id: 'reason_for_referral', label: 'Reason for Referral' },
  ];
  const referralValue = (id) => {
    const v = intakeData[id] !== undefined && intakeData[id] !== null && intakeData[id] !== ''
      ? intakeData[id]
      : (editingClient || {})[id];
    return (v === undefined || v === null) ? '' : String(v);
  };
  const missingReferral = referralFieldDefs.filter(
    d => !configRenderedIds.has(d.id) && referralValue(d.id).trim() !== ''
  );
  if (missingReferral.length > 0) {
    const referralRows = [];
    for (let i = 0; i < missingReferral.length; i += 3) {
      referralRows.push(
        missingReferral.slice(i, i + 3).map(d => ({ label: d.label, value: referralValue(d.id) }))
      );
    }
    addSection('Referral Information', referralRows);
  }
  
  // Add Insurance Section from the new insurance management system
    const client = editingClient || {};
    const activeInsurances = (client.insurances || [])
      .filter(ins => ins.status === 'active')
      .sort((a, b) => a.priority - b.priority);
    
    // Add Pharmacy Information if available
    if (intakeData.pharmacy_name || client.pharmacy_name) {
      const pharmacyRows = [
        [
          { label: 'Pharmacy Name', value: intakeData.pharmacy_name || client.pharmacy_name },
          { label: 'Phone', value: intakeData.pharmacy_phone || client.pharmacy_phone },
          { label: 'Fax', value: intakeData.pharmacy_fax || client.pharmacy_fax }
        ]
      ];
      if (intakeData.pharmacy_address || client.pharmacy_address) {
        pharmacyRows.push([
          { label: 'Address', value: intakeData.pharmacy_address || client.pharmacy_address },
          { label: '', value: '' },
          { label: '', value: '' }
        ]);
      }
      addSection('Pharmacy Information', pharmacyRows);
    }
    
    // Add Current Medications if available
    const medications = intakeData.medications || client.medications || [];
    if (medications.length > 0) {
      const medRows = medications.map(med => [
        { label: 'Medication', value: med.medication_name || med.name },
        { label: 'Dosage', value: med.dosage || 'N/A' },
        { label: 'Frequency', value: med.frequency || 'N/A' }
      ]);
      addSection('Current Medications', medRows);
    }
  
  if (activeInsurances.length > 0) {
    const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary'};
    
    activeInsurances.forEach((insurance) => {
      const priorityLabel = priorityLabels[insurance.priority] || `Priority ${insurance.priority}`;
      const insuranceRows = [];
      
      // Row 1: Company, Plan, Member ID
      insuranceRows.push([
        { label: 'Insurance Company', value: insurance.company },
        { label: 'Plan', value: insurance.plan || 'N/A' },
        { label: 'Member ID', value: insurance.member_id }
      ]);
      
      // Row 2: Group Number, Relationship, Eligibility
      insuranceRows.push([
        { label: 'Group Number', value: insurance.group_number || 'N/A' },
        { label: 'Relationship', value: insurance.relationship_to_insured || 'Self' },
        { label: 'Eligibility', value: insurance.eligibility_status || 'Pending' }
      ]);
      
      // Row 3: Copay, Deductible, Phone
      const copayText = insurance.copay_amount ? `$${parseFloat(insurance.copay_amount).toFixed(2)}` : 'N/A';
      const deductibleText = insurance.deductible_amount ? `$${parseFloat(insurance.deductible_amount).toFixed(2)}` : 'N/A';
      insuranceRows.push([
        { label: 'Copay', value: copayText },
        { label: 'Deductible', value: deductibleText },
        { label: 'Phone', value: insurance.phone || 'N/A' }
      ]);
      
      // Row 4: Effective Date, End Date
      if (insurance.start_date || insurance.end_date) {
        insuranceRows.push([
          { label: 'Effective Date', value: insurance.start_date ? new Date(insurance.start_date).toLocaleDateString() : 'N/A' },
          { label: 'End Date', value: insurance.end_date ? new Date(insurance.end_date).toLocaleDateString() : 'N/A' },
          { label: '', value: '' }
        ]);
      }
      
      // Row 5: Insured Person Info (if not self)
      if (insurance.relationship_to_insured !== 'self' && insurance.insured_name) {
        insuranceRows.push([
          { label: 'Insured Person Name', value: insurance.insured_name },
          { label: 'Insured SSN', value: insurance.insured_ssn || 'N/A' },
          { label: 'Insured DOB', value: insurance.insured_dob ? new Date(insurance.insured_dob).toLocaleDateString() : 'N/A' }
        ]);
      }
      
      // Row 6: MSP Type Code (if Medicare is secondary or lower)
      if (insurance.company?.toLowerCase().includes('medicare') && insurance.priority >= 2 && insurance.msp_type_code) {
        const mspDescriptions = {
          '12': 'Working Aged - EGHP',
          '13': 'End-Stage Renal Disease (ESRD)',
          '14': 'Auto/No-Fault Insurance',
          '15': 'Workers\' Compensation',
          '16': 'Other Liability Insurance',
          '41': 'Black Lung',
          '42': 'Veterans Administration (VA)',
          '43': 'Public Health Service/Federal Agency',
          '47': 'Disability - Large Group Health Plan',
          '48': 'ESRD + Working Aged/Disability'
        };
        insuranceRows.push([
          { label: 'MSP Type Code', value: insurance.msp_type_code },
          { label: 'MSP Reason', value: mspDescriptions[insurance.msp_type_code] || 'N/A' },
          { label: '', value: '' }
        ]);
      }
      
      addSection(`${priorityLabel} Insurance`, insuranceRows);
    });
  } else {
    // No insurance - show Self Pay
    addSection('Insurance Information', [
      [
        { label: 'Payment Type', value: 'Self Pay' },
        { label: '', value: '' },
        { label: '', value: '' }
      ]
    ]);
  }
  
  // Add branded footer to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addPdfOrgFooter(doc, brandingSettings);
    // Add page number
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, { align: 'right' });
  }
  
  // Generate filename with creation date: LastName, FirstName - Referral MM-DD-YYYY.pdf
  const referralClient = { 
    full_name: `${intakeData.first_name || editingClient?.first_name || ''} ${intakeData.last_name || editingClient?.last_name || ''}`.trim() || 'Unknown Client' 
  };
  const referralDate = formatDateForFilename(new Date());
  const fileName = generateDownloadFilename(referralClient, `Referral ${referralDate}`);
  
  // Get PDF as blob with explicit type
  const pdfOutput = doc.output('blob');
  const pdfBlob = new Blob([pdfOutput], { type: 'application/pdf' });
  
  return { blob: pdfBlob, fileName, currentDate };
};
